import React, { useContext } from 'react';
import { GridApi, GridOptions } from 'ag-grid-community';
import { formatPostDate } from '@mummssoftware/utils/formatters';
import { find } from 'lodash-es';
import {
  hospiceDataAdmit,
  hospiceDataDischarge,
  Approval,
  Hisadmineventdto,
  Cmsapproved,
  Daystoadmitdeadline,
  Daystodischargedeadline,
  Download,
  vertical,
  valueByType,
  getStatusData,
  getRowClass,
} from './constants';
import { SENT, ACCPT, ADM, DIS } from '../Context/constants';
import { QuotaCellRenderer } from './CellRenderer';
import { HISContext } from '../Context/HISContext';
import { Context } from '../../context';
import { AgGridTable } from './Table';
import { TableMode } from './types';
import { MessageOverlay } from './MessageOverlay';
import defaults from '../../translations/en.json';

interface HospiceItemSetTableProps {
  viewModePrint?: boolean;
  mode?: TableMode;
}

const frameworkComponents = {
  quotaCellRenderer: QuotaCellRenderer,
  customLoadingOverlay: MessageOverlay,
};

export const HospiceItemSmallTable = ({
  viewModePrint,
  mode,
}: HospiceItemSetTableProps) => {
  const { hospiceInfo, patient, currentUser, siteId, postHISData } = useContext(
    Context,
  );
  const { individualPatientHeader, individualPatientHIS } = useContext(
    HISContext,
  );

  const patientInfo = patient || individualPatientHeader;
  const HISInfo = hospiceInfo || individualPatientHIS;

  const { patientNumber, dischargeDate, episodes } = patientInfo;
  const { personNumber } = currentUser;
  const isDischargedPatient = !!dischargeDate;
  // Any patient in a program besides Hospice should display a message over the HIS Widget
  const isNotHPPatient = episodes[episodes.length - 1].programId !== -1;
  const showOverlay =
    HISInfo.bshowadminactionbuttons === 'false' || isNotHPPatient;
  const overlayAlert = isNotHPPatient
    ? defaults.noHIPPatient
    : HISInfo.demographicsstatus;

  const hospiceData = HISInfo.hisadmineventdto;

  const dischargePatientStatus =
    hospiceData &&
    find(hospiceData, {
      hisadminstatusshortname: ACCPT,
      type: ADM,
    })
      ? DIS
      : ADM;

  const postData = (
    shortName: string,
    method: string,
    type: mumms.patientStatus,
  ) => {
    const dataPost = {
      personnumber: personNumber,
      patientnumber: patientNumber,
      siteid: siteId || patientInfo.siteId.toString(),
      statusdatetime: formatPostDate(Date.now()),
      hisadminstatusshortname: shortName, // 'DWN'
      type,
    };
    postHISData(patientNumber, dataPost, method, type); // should it be PUT at times?
  };

  let tableData;
  if (mode === vertical) {
    const tableDataType = isDischargedPatient
      ? [...hospiceDataAdmit, ...hospiceDataDischarge]
      : hospiceDataAdmit;

    tableData = tableDataType.map((entry) => ({
      ...entry,
      stage: {
        value:
          entry.key === Hisadmineventdto
            ? valueByType(HISInfo[entry.key as mumms.HISkey])
            : HISInfo[entry.key as mumms.HISkey],
      },
    }));
  } else {
    const data = { ...HISInfo };
    let newData;
    if (isDischargedPatient) {
      newData = getStatusData(
        data,
        dischargePatientStatus,
        Daystodischargedeadline,
      );
    } else {
      newData = getStatusData(data, ADM, Daystoadmitdeadline);
    }
    tableData = [newData];
  }

  const getVerticalCellClass = (params: any) => {
    const { title, type } = params.data;
    const value = params.value.value[type] || [];
    const className = getRowClass(type);
    if (title === Approval) {
      return value.length ? className : 'health no-border';
    }
    if (title === Download) {
      const sent = find(value, {
        hisadminstatusshortname: SENT,
      });
      return sent ? className : 'health no-border';
    }
    if (title === Cmsapproved) {
      const cmsAccepted = find(value, {
        hisadminstatusshortname: ACCPT,
      });
      return cmsAccepted ? className : 'health no-border';
    }
    return 'health no-border';
  };

  const getHorizontalCellClass = (params: any, title: string) => {
    const { type } = params.data;
    const { value } = params.value;
    const className = getRowClass(type);
    const valueStatus = value && value[type] ? value[type] : [];

    if (title === Approval) {
      return valueStatus.length ? className : 'health no-border';
    }
    if (title === Download) {
      const sent = find(valueStatus, {
        hisadminstatusshortname: SENT,
      });
      return sent ? className : 'health no-border';
    }
    if (title === Cmsapproved) {
      const cmsAccepted = find(valueStatus, {
        hisadminstatusshortname: ACCPT,
      });
      return cmsAccepted ? className : 'health no-border';
    }
    return 'health no-border';
  };

  let columnTableDefs;
  if (mode === vertical) {
    columnTableDefs = [
      {
        headerName: defaults.his,
        field: 'name',
        cellClass: 'health no-border',
        suppressMenu: true,
        width: isDischargedPatient ? 185 : 155,
        suppressSizeToFit: true,
      },
      {
        headerName: defaults.stage,
        field: 'stage',
        cellRendererFramework: QuotaCellRenderer,
        cellClass: (params: any) => getVerticalCellClass(params),
        headerClass: 'header-center',
        cellRendererParams: {
          postData,
          mode,
        },
        suppressMenu: true,
      },
    ];
  } else {
    columnTableDefs = [
      {
        headerName: defaults.approval,
        field: Hisadmineventdto,
        cellClass: (params: any) => getHorizontalCellClass(params, Approval),
        cellRendererFramework: QuotaCellRenderer,
        suppressMenu: true,
        cellRendererParams: {
          postData,
          mode,
          title: Approval,
        },
      },
      {
        headerName: defaults.downloadSend,
        field: Hisadmineventdto,
        cellClass: (params: any) => getHorizontalCellClass(params, Download),
        cellRendererFramework: QuotaCellRenderer,
        suppressMenu: true,
        cellRendererParams: {
          postData,
          title: Download,
          mode,
        },
      },
      {
        headerName: defaults.cmsaccepted,
        field: Hisadmineventdto,
        cellClass: (params: any) => getHorizontalCellClass(params, Cmsapproved),
        cellRendererFramework: QuotaCellRenderer,
        suppressMenu: true,
        cellRendererParams: {
          postData,
          title: Cmsapproved,
          mode,
        },
      },
      {
        headerName: isDischargedPatient
          ? defaults.daystodischargedeadline
          : defaults.daystodeadline,
        field: Daystoadmitdeadline,
        cellClass: 'health no-border',
        cellRendererFramework: QuotaCellRenderer,
        suppressMenu: true,
        cellRendererParams: {
          title: Daystoadmitdeadline,
          mode,
        },
      },
    ];
  }

  const gridOptions: GridOptions = {
    defaultColDef: {
      resizable: true,
    },
    columnDefs: columnTableDefs,
    onGridReady: (params: any) => {
      const gridApi: GridApi = params.api;
      gridApi.sizeColumnsToFit();
      showOverlay && gridApi.showLoadingOverlay();
      if (viewModePrint) {
        gridOptions.domLayout = 'print';
      }
    },
  };
  return (
    <AgGridTable
      gridOptions={gridOptions}
      rowData={tableData || []}
      frameworkComponents={frameworkComponents}
      mode={mode}
      loadingOverlayComponent="customLoadingOverlay"
      loadingOverlayComponentParams={{
        loadingMessage: overlayAlert,
      }}
    />
  );
};

export default HospiceItemSmallTable;
